// contexts/OpenLeakContext.tsx
import React, { ReactNode, createContext, useContext, useEffect, useState, useCallback } from 'react';
import { UserContext } from "features/account";
import { DataLeak, LeakFilters, LeakStats, FollowAlert } from '../types/openLeaks.types';
import {
  getLeaks,
  getLeakById,
  getLeaksStats,
  createFollowAlert,
  deleteFollowAlert,
  getFollowAlertsByEmail,
  checkCompanyLeaks
} from '../api';

interface OpenLeakContextType {
  // State
  leaks: DataLeak[];
  totalLeaks: number;
  stats: LeakStats | null;
  followAlerts: FollowAlert[];
  loading: boolean;
  error: string | null;
  filters: LeakFilters;

  // Actions
  setFilters: (filters: LeakFilters) => void;
  resetFilters: () => void;
  fetchLeaks: () => Promise<void>;
  fetchLeakById: (id: number) => Promise<DataLeak | null>;
  fetchStats: () => Promise<void>;
  fetchFollowAlerts: (email: string) => Promise<void>;
  addFollowAlert: (email: string, company: string) => Promise<FollowAlert | null>;
  removeFollowAlert: (id: number) => Promise<boolean>;
  checkCompany: (company: string) => Promise<DataLeak[]>;
  clearError: () => void;
}

const OpenLeakContext = createContext<OpenLeakContextType>({
  leaks: [],
  totalLeaks: 0,
  stats: null,
  followAlerts: [],
  loading: false,
  error: null,
  filters: {},
  setFilters: () => {},
  resetFilters: () => {},
  fetchLeaks: async () => {},
  fetchLeakById: async () => null,
  fetchStats: async () => {},
  fetchFollowAlerts: async () => {},
  addFollowAlert: async () => null,
  removeFollowAlert: async () => false,
  checkCompany: async () => [],
  clearError: () => {},
});

const OpenLeakContextProvider = ({ children }: { children: ReactNode }) => {
  const { user } = useContext(UserContext);

  // State
  const [leaks, setLeaks] = useState<DataLeak[]>([]);
  const [totalLeaks, setTotalLeaks] = useState<number>(0);
  const [stats, setStats] = useState<LeakStats | null>(null);
  const [followAlerts, setFollowAlerts] = useState<FollowAlert[]>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const [filters, setFilters] = useState<LeakFilters>({});

  const clearError = () => setError(null);

  /**
   * Récupérer les fuites avec les filtres actuels
   */
  const fetchLeaks = useCallback(async () => {
    setLoading(true);
    setError(null);

    try {
      const response = await getLeaks(filters);
      setLeaks(response?.data || []);
      setTotalLeaks(response?.total || 0);
    } catch (err: any) {
      console.error('Erreur fetchLeaks:', err);
      setError(err.message || 'Erreur lors du chargement des fuites');
      setLeaks([]);
      setTotalLeaks(0);
    } finally {
      setLoading(false);
    }
  }, [filters]);

  /**
   * Récupérer une fuite par ID
   */
  const fetchLeakById = useCallback(async (id: number): Promise<DataLeak | null> => {
    setLoading(true);
    setError(null);

    try {
      const response = await getLeakById(id);
      return response?.data || null;
    } catch (err: any) {
      console.error(`Erreur fetchLeakById ${id}:`, err);
      setError(err.message || 'Erreur lors du chargement de la fuite');
      return null;
    } finally {
      setLoading(false);
    }
  }, []);

  /**
   * Récupérer les statistiques
   */
  const fetchStats = useCallback(async () => {
      setLoading(true);
      setError(null);

      try {
        const response = await getLeaksStats();

        // Si l'API ne retourne rien, utilisez des données de test
        if (!response || (!response.byStatus && !response.totalLeaks)) {
          console.warn('Pas de données de stats, utilisation de données de test');
          setStats({
            totalLeaks: 0,
            totalAffected: 0,
            byStatus: {},
            byMonth: {},
            topCompanies: []
          });
        } else {
          setStats(response);
        }
      } catch (err: any) {
        console.error('Erreur fetchStats:', err);
        // Données de test en cas d'erreur
        setStats({
          totalLeaks: 0,
          totalAffected: 0,
          byStatus: {},
          byMonth: {},
          topCompanies: []
        });
      } finally {
        setLoading(false);
      }
    }, []);
  /**
   * Récupérer les alertes d'un email
   */
  const fetchFollowAlerts = useCallback(async (email: string) => {
    if (!email) {
      setFollowAlerts([]);
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const response = await getFollowAlertsByEmail(email);
      setFollowAlerts(response?.data || []);
    } catch (err: any) {
      console.error('Erreur fetchFollowAlerts:', err);
      setError(err.message || 'Erreur lors du chargement des alertes');
      setFollowAlerts([]);
    } finally {
      setLoading(false);
    }
  }, []);

  /**
   * Ajouter une alerte de suivi
   */
  const addFollowAlert = useCallback(async (email: string, company: string): Promise<FollowAlert | null> => {
    setLoading(true);
    setError(null);

    try {
      const response = await createFollowAlert(email, company);
      const newAlert = response?.data;

      if (newAlert) {
        setFollowAlerts(prev => [...prev, newAlert]);
      }

      return newAlert;
    } catch (err: any) {
      console.error('Erreur addFollowAlert:', err);
      setError(err.message || 'Erreur lors de l\'ajout de l\'alerte');
      return null;
    } finally {
      setLoading(false);
    }
  }, []);

  /**
   * Supprimer une alerte
   */
  const removeFollowAlert = useCallback(async (id: number): Promise<boolean> => {
    setLoading(true);
    setError(null);

    try {
      await deleteFollowAlert(id);
      setFollowAlerts(prev => prev.filter(alert => alert.id !== id));
      return true;
    } catch (err: any) {
      console.error(`Erreur removeFollowAlert ${id}:`, err);
      setError(err.message || 'Erreur lors de la suppression de l\'alerte');
      return false;
    } finally {
      setLoading(false);
    }
  }, []);

  /**
   * Vérifier les fuites d'une entreprise
   */
  const checkCompany = useCallback(async (company: string): Promise<DataLeak[]> => {
    setLoading(true);
    setError(null);

    try {
      const response = await checkCompanyLeaks(company);
      return response?.data || [];
    } catch (err: any) {
      console.error(`Erreur checkCompany ${company}:`, err);
      setError(err.message || 'Erreur lors de la vérification');
      return [];
    } finally {
      setLoading(false);
    }
  }, []);

  /**
   * Réinitialiser les filtres
   */
  const resetFilters = useCallback(() => {
    setFilters({});
  }, []);

  // Chargement initial des statistiques
  useEffect(() => {
    fetchStats();
  }, [fetchStats]);

  // Chargement des fuites quand les filtres changent
  useEffect(() => {
    fetchLeaks();
  }, [fetchLeaks]);

  const value = {
    // State
    leaks,
    totalLeaks,
    stats,
    followAlerts,
    loading,
    error,
    filters,

    // Actions
    setFilters,
    resetFilters,
    fetchLeaks,
    fetchLeakById,
    fetchStats,
    fetchFollowAlerts,
    addFollowAlert,
    removeFollowAlert,
    checkCompany,
    clearError,
  };

  return (
    <OpenLeakContext.Provider value={value}>
      {children}
    </OpenLeakContext.Provider>
  );
};

export { OpenLeakContext, OpenLeakContextProvider };